import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../ts/Glob';
import { DirsWorkspacesInner } from './DirsWorkspacesInner';
import { DirsTemplatesInner } from './DirsTemplatesInner';
import * as reactGlobalClick from '../../../_common/platform/ts/reactGlobalClick';
import WidgetNotifications from './notifications/WidgetNotifications';
import { config as commonConfig } from '../../../pdc-platform/src/ts/config';
import { useOverflowHook } from '../../../_common/platform/tsx/mixins/OverflowMixin';

import {
    getStyles,
    IResizeAndMoveProperties,
    useResizeAndMoveHook
} from '../../../_common/platform/tsx/mixins/ResizeMove';

import {
    DialogType,
    IDialog,
    IPopup,
    IDialogConfig,
    IDialogTemplateDirSelect,
    IDialogWorkspaceDirSelect,
    IDialogComponent
} from '../ts/actions/DialogActions';

const DIALOG_TYPE_DICT_ENUM = {
    [DialogType.CONFIRM]: 'confirm',
    [DialogType.WORKSPACE_DIR_SELECT]: 'workspaceDirSelect',
    [DialogType.TEMPLATE_DIR_SELECT]: 'templateDirSelect',
    [DialogType.CUSTOM]: 'custom',
    [DialogType.FULL_SCREEN]: 'fullScreen',
    [DialogType.NEW_CUSTOM]: 'newCustom'
};

function getOverflowContainerBodyStyle(containerSize: number, fullHeight?: boolean) {
    const result: React.CSSProperties = {};

    if (containerSize > 0) {
        result.maxHeight = containerSize;
    }

    if (fullHeight) {
        result.height = result.maxHeight;
    }

    return result;
}

function getZIndex(obj: ICommonDialogObject<ICommonDialogConfig>) {
    return obj.config != null && obj.config.zIndex != null ? obj.config.zIndex : obj.zIndex;
}

interface ICommonDialogConfig extends IDialogConfig {
    confirmTitle?: string;
    cancelTitle?: string;
    closeTitle?: string;

    content?;
}

interface ICommonDialogObject<TConfig extends ICommonDialogConfig> {
    zIndex: number;
    title?: string;
    text?: string;
    config?: TConfig;
}

interface IConfirmDialogProperties extends IResizeAndMoveProperties {
    obj?: ICommonDialogObject<ICommonDialogConfig>;

    onSuccess?(): void;
    onCancel?(): void;
    onFail?(): void;
}

function ConfirmDialog(props: IConfirmDialogProperties) {
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const footerRef = React.useRef<HTMLDivElement>(null);
    const { containerSize } = useResizeAndMoveHook(
        {
            draggable: true,
            resizable: false
        },
        'auto',
        'auto',
        dialogRef,
        headerRef,
        footerRef
    );

    const getStyle = () => {
        const { obj } = props;

        return {
            display: 'block',
            zIndex: getZIndex(obj)
        };
    };

    const obj = props.obj;
    const config = (obj && obj.config) || {};

    let confirmButton: React.JSX.Element;

    if (config.confirmTitle !== null) {
        confirmButton = (
            <button type="button" className="btn btn-success confirm-button" onClick={props.onSuccess}>
                {config.confirmTitle || 'Confirm'}
            </button>
        );
    }

    let denyButton: React.JSX.Element;

    if (config.cancelTitle != null) {
        denyButton = (
            <button type="button" className="btn btn-danger" onClick={props.onFail}>
                {config.cancelTitle || 'Deny'}
            </button>
        );
    }

    const bodyStyle: React.CSSProperties = {};

    if (containerSize.height > 0) {
        bodyStyle.maxHeight = containerSize.height;
    }

    return (
        <div className="modal fade show" style={getStyle()} role="dialog">
            <div className="modal-dialog">
                <div className="modal-content" ref={dialogRef}>
                    <div className="modal-header" style={{ cursor: 'move' }} ref={headerRef}>
                        <h4 className="modal-title">{obj.title}</h4>

                        <span
                            className="glyphicon glyphicon-remove"
                            style={{ cursor: 'pointer' }}
                            onClick={props.onCancel}
                        ></span>
                    </div>

                    <div className="modal-body container-fluid" style={bodyStyle}>
                        {obj.text}
                        {typeof config.content === 'function' ? config.content() : config.content}
                    </div>

                    <div className="modal-footer" ref={footerRef}>
                        <div className="btn-group" role="group" aria-label="Save Buttons">
                            {confirmButton}
                            {denyButton}
                            <button type="button" className="btn btn-default" onClick={props.onCancel}>
                                {config.closeTitle || 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export interface ICustomDialogSharedState {
    isValid?: boolean;
    validResult?: string;
}

export interface ICustomDialogObjectConfig extends ICommonDialogConfig {
    dialogClassName?: string;
    confirm?: boolean;
    cancel?: boolean;
    deny?: boolean;
    fullHeight?: boolean;
    inlineForm?: boolean;
    noShadow?: boolean;

    componentProps?;
    componentOptions?;

    HeaderComponent?;
    headerComponentOptions?;
    headerComponentProps?;

    getInitialSharedState?(): ICustomDialogSharedState;

    Component?: IDialogComponent;
    FooterComponent?;
    footerComponentOptions?;
    footerComponentProps?;
}

export interface ICustomDialogObject extends ICommonDialogObject<ICustomDialogObjectConfig> {
    setConfirmValueGetter?;
    unsetConfirmValueGetter?;
    active?: boolean;
}

export interface ICustomDialogProperties extends IResizeAndMoveProperties {
    obj: ICustomDialogObject;
    pinNavigationBar?: boolean;
    glob: IGlob;

    success?(...arg): void;
    fail?(...arg): void;
    close?(...arg): void;
}

export function CustomDialog(props: ICustomDialogProperties) {
    const [containerSize, setContainerSize] = React.useState(0);
    const [resized, setResized] = React.useState(false);

    const [sharedState, setSharedState] = React.useState<ICustomDialogSharedState>(() => {
        const config = props.obj.config || ({} as ICustomDialogObjectConfig);

        if (typeof config.getInitialSharedState === 'function') {
            return config.getInitialSharedState() || {};
        } else {
            return {};
        }
    });

    const modalDialogRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const bodyRef = React.useRef<HTMLDivElement>(null);
    const footerRef = React.useRef<HTMLDivElement>(null);

    const handleSetContainerSize = () => {
        //IL 81906: HTML, embedded GRID, Config | Options TAB: the dialog size is 5x time higher than screen = BUG
        if (props.disableOverflow || resized) {
            return;
        }

        let newContainerSize = $(modalDialogRef.current).height(); //IL:81682: Resizing of a dialog with CKEditor inside works bad = BUG

        if (headerRef.current != null) {
            newContainerSize -= $(headerRef.current).outerHeight();
        }

        if (footerRef.current != null) {
            newContainerSize -= $(footerRef.current).outerHeight();
        }

        setContainerSize(newContainerSize);

        $('.listen-container-resize', contentRef.current).trigger('container-resize', [newContainerSize]);
    };

    const handleSetContentSize = React.useCallback(() => {
        if (props.disableOverflow || resized) {
            return;
        }

        let newContainerSize = $(contentRef.current).height(); //$(modalDialogRef.current).height(); //IL:81682: Resizing of a dialog with CKEditor inside works bad = BUG

        if (headerRef.current != null) {
            newContainerSize -= $(headerRef.current).outerHeight();
        }

        if (footerRef.current != null) {
            newContainerSize -= $(footerRef.current).outerHeight();
        }

        setContainerSize(newContainerSize);

        $('.listen-container-resize', contentRef.current).trigger('container-resize', [newContainerSize]);
    }, [resized]);

    React.useEffect(() => {
        const resizable = props.resizable == null || props.resizable === true || typeof props.resizable === 'string';

        if (props.obj.config.content != null) {
            $(bodyRef.current).append(props.obj.config.content);
        }

        if (contentRef.current != null) {
            const content = $(contentRef.current);

            if (typeof content.resizable === 'function') {
                //VR: check via 'typeof' for JEST testing
                if (props.draggable) {
                    content.draggable({
                        handle: '.modal-header'
                    });
                }

                if (resizable) {
                    let handles = 'all';

                    if (props.resizable === 'height') {
                        handles = 's';
                    }

                    if (props.resizable === 'width') {
                        handles = 'e';
                    }

                    const handleOnStartResize = () => {
                        handleSetContentSize();
                        setResized(true);
                    };

                    content.resizable({
                        start: handleOnStartResize,
                        resize: handleSetContentSize,
                        handles: handles
                    });
                }
            }
        }

        if (!props.disableOverflow) {
            handleSetContainerSize(); //IL: 81682: Resizing of a dialog with CKEditor inside works bad = BUG
            //IL: 81906: HTML, embedded GRID, Config | Options TAB: the dialog size is 5x time higher than screen = BUG

            if (resizable) {
                $(window).on('resize', handleSetContentSize);
            }
        }

        return () => {
            if (contentRef.current != null) {
                const content = $(contentRef.current);

                if (typeof content.resizable === 'function') {
                    if (props.draggable) {
                        content.draggable('destroy');
                    }

                    if (resizable) {
                        content.resizable('destroy');
                    }
                }
            }

            if (!props.disableOverflow) {
                if (resizable) {
                    $(window).off('resize', handleSetContentSize);
                }
            }
        };
    }, []);

    const handleOnSharedStateUpdate = (value) => {
        setSharedState({
            ...sharedState,
            ...value
        });
    };

    const getComponent = (Component, options, configProps) => {
        return (
            <Component
                setConfirmValueGetter={props.obj.setConfirmValueGetter}
                unsetConfirmValueGetter={props.obj.unsetConfirmValueGetter}
                options={options}
                active={props.obj.active}
                setContainerSize={handleSetContentSize}
                sharedState={sharedState}
                onSharedStateUpdate={handleOnSharedStateUpdate}
                success={props.success}
                fail={props.fail}
                close={props.close}
                glob={props.glob}
                {...configProps}
            />
        );
    };

    const obj = props.obj;
    const config = obj.config || ({} as ICustomDialogObjectConfig);

    let confirmButton: React.JSX.Element;
    let denyButton: React.JSX.Element;
    let cancelButton: React.JSX.Element;
    let elementText: React.JSX.Element;
    let style: React.CSSProperties = {};

    if (config.confirm !== false) {
        let className = 'btn btn-success confirm-button';
        let disabled = false;

        if (sharedState.isValid === false) {
            className += ' disabled';
            style.opacity = '0.3';
            disabled = true;
        }

        confirmButton = (
            <button type="button" disabled={disabled} className={className} style={style} onClick={props.success}>
                {config.confirmTitle || 'Confirm'}
            </button>
        );
    }

    if (config.deny !== false) {
        denyButton = (
            <button type="button" className="btn btn-danger" onClick={props.fail}>
                {config.cancelTitle || 'Deny'}
            </button>
        );
    }

    if (config.cancel !== false) {
        cancelButton = (
            <button type="button" className="btn btn-default" onClick={props.close}>
                {config.closeTitle || 'Cancel'}
            </button>
        );
    }

    let saveButtons: React.JSX.Element;

    if (confirmButton != null || denyButton != null || cancelButton != null) {
        saveButtons = (
            <div className="btn-group" role="group" aria-label="Save Buttons">
                {confirmButton}
                {denyButton}
                {cancelButton}
            </div>
        );
    }

    if (obj.text != null && obj.text !== '') {
        elementText = (
            <div
                style={{
                    marginBottom: '15px'
                }}
            >
                {obj.text}
            </div>
        );
    }

    const Component = config.Component;
    const HeaderComponent = config.HeaderComponent;
    const FooterComponent = config.FooterComponent;

    let content: React.JSX.Element;
    let headerContent: React.JSX.Element;
    let footerComponent: React.JSX.Element;

    if (Component != null) {
        content = getComponent(Component, config.componentOptions, config.componentProps);
    }

    if (sharedState.validResult != null) {
        const validResult = sharedState.validResult;

        config.headerComponentProps = _.extend({}, config.headerComponentProps, { validResult: validResult });
    }

    if (HeaderComponent != null) {
        headerContent = getComponent(HeaderComponent, config.headerComponentOptions, config.headerComponentProps);
    }

    if (FooterComponent != null) {
        footerComponent = getComponent(FooterComponent, config.footerComponentOptions, config.footerComponentProps);
    }

    const fadeStyle: React.CSSProperties = {
        display: 'block',
        zIndex: getZIndex(obj)
    };

    if (props.pinNavigationBar && !config.inlineForm && config.tabID != null) {
        const lastNavigationBarHeight = props.glob.AppStore.getLastNavigationBarHeight();

        if (lastNavigationBarHeight > 0) {
            fadeStyle.top = lastNavigationBarHeight + 'px';
        }
    }

    const dialogStyle: React.CSSProperties = {};

    if (commonConfig.LAYOUT_TYPE !== 'narrow') {
        if (config.minWidth != null) {
            dialogStyle.minWidth = config.minWidth;
        }

        if (config.minHeight != null) {
            dialogStyle.minHeight = config.minHeight;
        }
    }

    const dialogClass = config.inlineForm ? '' : 'modal fade show';
    const contentStyle: React.CSSProperties = { minWidth: 'inherit', minHeight: 'inherit' };

    if (config.noShadow) {
        contentStyle['boxShadow'] = 'none';
    }

    const bodyStyle = getOverflowContainerBodyStyle(containerSize, config.fullHeight);

    return (
        <div className={dialogClass} style={fadeStyle} role="dialog">
            <div
                className={'modal-dialog mw-100' + (config.dialogClassName ? ' ' + config.dialogClassName : '')}
                style={dialogStyle}
                ref={modalDialogRef}
            >
                <div className="modal-content" style={contentStyle} ref={contentRef}>
                    <div className="modal-header" style={{ cursor: 'move' }} ref={headerRef}>
                        <h4 className="modal-title">{obj.title}</h4>
                        {headerContent}
                        {config.inlineForm ? null : (
                            <span
                                className="glyphicon glyphicon-remove"
                                style={{ cursor: 'pointer' }}
                                onClick={props.close}
                            ></span>
                        )}
                    </div>

                    <div className="modal-body container-fluid" style={bodyStyle} ref={bodyRef}>
                        <WidgetNotifications runTimeWidgetID={null} showInPopup={true} glob={props.glob} />
                        {elementText}
                        {content}
                    </div>

                    <div className="modal-footer" ref={footerRef}>
                        {footerComponent}
                        {saveButtons}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function NewCustomDialog(props: ICustomDialogProperties) {
    const [sharedState, setSharedState] = React.useState<ICustomDialogSharedState>(() => {
        const config = props.obj.config || ({} as ICustomDialogObjectConfig);

        if (typeof config.getInitialSharedState === 'function') {
            return config.getInitialSharedState() || {};
        } else {
            return {};
        }
    });

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const footerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (props.obj.config.content != null) {
            $(contentRef.current).append(props.obj.config.content);
        }
    }, []);

    const config = props.obj.config ?? ({} as ICustomDialogObjectConfig);
    const draggable = !config.inlineForm && props.draggable !== false;

    const { containerSize, initialContainerHeight, initialContentWidth, position } = useResizeAndMoveHook(
        {
            dialogName: config.name ?? props.dialogName,
            draggable: draggable,
            resizable: props.resizable ?? (config.inlineForm ? 'height' : true),
            disableOverflow: props.disableOverflow
        },
        config.defaultHeight ?? (config.inlineForm ? '1/4' : '1/2'),
        config.defaultWidth ?? (config.inlineForm ? '100%' : '1/2'),
        contentRef,
        headerRef,
        footerRef
    );
    const containerHeight = useOverflowHook(
        props.disableOverflow,
        containerSize.height,
        containerRef,
        headerRef,
        footerRef
    );

    const handleOnSharedStateUpdate = (value) => {
        setSharedState({
            ...sharedState,
            ...value
        });
    };

    const getComponent = (Component: IDialogComponent, options, configProps) => {
        return (
            <Component
                setConfirmValueGetter={props.obj.setConfirmValueGetter}
                unsetConfirmValueGetter={props.obj.unsetConfirmValueGetter}
                options={options}
                active={props.obj.active}
                //setContainerSize={handleOnResize}
                sharedState={sharedState}
                onSharedStateUpdate={handleOnSharedStateUpdate}
                success={props.success}
                fail={props.fail}
                close={props.close}
                glob={props.glob}
                {...configProps}
            />
        );
    };

    const obj = props.obj;

    let confirmButton: React.JSX.Element;
    let denyButton: React.JSX.Element;
    let cancelButton: React.JSX.Element;
    let elementText: React.JSX.Element;
    let style: React.CSSProperties = {};

    if (config.confirm !== false) {
        let className = 'btn btn-success confirm-button';
        let disabled = false;

        if (sharedState.isValid === false) {
            className += ' disabled';
            style.opacity = '0.3';
            disabled = true;
        }

        confirmButton = (
            <button type="button" disabled={disabled} className={className} style={style} onClick={props.success}>
                {config.confirmTitle || 'Confirm'}
            </button>
        );
    }

    if (config.deny !== false) {
        denyButton = (
            <button type="button" className="btn btn-danger" onClick={props.fail}>
                {config.cancelTitle || 'Deny'}
            </button>
        );
    }

    if (config.cancel !== false) {
        cancelButton = (
            <button type="button" className="btn btn-default" onClick={props.close}>
                {config.closeTitle || 'Cancel'}
            </button>
        );
    }

    let saveButtons: React.JSX.Element;

    if (confirmButton != null || denyButton != null || cancelButton != null) {
        saveButtons = (
            <div className="btn-group" role="group" aria-label="Save Buttons">
                {confirmButton}
                {denyButton}
                {cancelButton}
            </div>
        );
    }

    if (obj.text != null && obj.text !== '') {
        elementText = (
            <div
                style={{
                    marginBottom: '15px'
                }}
            >
                {obj.text}
            </div>
        );
    }

    const Component = config.Component;
    const HeaderComponent = config.HeaderComponent;
    const FooterComponent = config.FooterComponent;

    let content: React.JSX.Element;
    let headerContent: React.JSX.Element;
    let footerComponent: React.JSX.Element;

    if (Component != null) {
        content = getComponent(Component, config.componentOptions, config.componentProps);
    }

    if (sharedState.validResult != null) {
        const validResult = sharedState.validResult;

        config.headerComponentProps = _.extend({}, config.headerComponentProps, { validResult: validResult });
    }

    if (HeaderComponent != null) {
        headerContent = getComponent(HeaderComponent, config.headerComponentOptions, config.headerComponentProps);
    }

    if (FooterComponent != null) {
        footerComponent = getComponent(FooterComponent, config.footerComponentOptions, config.footerComponentProps);
    }

    const { modalStyle, containerStyle, contentStyle, bodyStyle } = getStyles(
        initialContainerHeight,
        initialContentWidth,
        containerSize.height,
        containerHeight,
        position,
        false,
        undefined,
        getZIndex(obj)
    );

    if (config.noShadow) {
        contentStyle.boxShadow = 'none';
    }

    const dialogClass = config.inlineForm ? '' : 'modal fade show';

    if (props.pinNavigationBar && !config.inlineForm && config.tabID != null) {
        const lastNavigationBarHeight = props.glob.AppStore.getLastNavigationBarHeight();

        if (lastNavigationBarHeight > 0) {
            modalStyle.top = lastNavigationBarHeight + 'px';
        }
    }

    let modalHeaderStyle: React.CSSProperties;

    if (draggable) {
        modalHeaderStyle = { cursor: 'move' };
    }

    return (
        <div className={dialogClass} style={modalStyle} role="dialog">
            <div
                className={'modal-dialog mw-100' + (config.dialogClassName ? ' ' + config.dialogClassName : '')}
                style={containerStyle}
                ref={containerRef}
            >
                <div className="modal-content" style={contentStyle} ref={contentRef}>
                    <div className="modal-header" style={modalHeaderStyle} ref={headerRef}>
                        <h4 className="modal-title">{obj.title}</h4>
                        {headerContent}
                        {config.inlineForm ? null : (
                            <span
                                className="glyphicon glyphicon-remove"
                                style={{ cursor: 'pointer' }}
                                onClick={props.close}
                            ></span>
                        )}
                    </div>

                    <div className="modal-body container-fluid" style={bodyStyle}>
                        <WidgetNotifications runTimeWidgetID={null} showInPopup={true} glob={props.glob} />
                        {elementText}
                        {content}
                    </div>

                    <div className="modal-footer" ref={footerRef}>
                        {footerComponent}
                        {saveButtons}
                    </div>
                </div>
            </div>
        </div>
    );
}

interface IPopupDialogProperties extends IResizeAndMoveProperties {
    id: string;
    obj: IPopup;
    glob: IGlob;
}

function DialogPopup(props: IPopupDialogProperties) {
    const modalFadeRef = React.useRef<HTMLDivElement>(null);
    const dialogRef = React.useRef<HTMLDivElement>(null);

    const { obj } = props;
    const { options } = obj;
    const windowHeight = $(document.body).height();
    const sizesCanBeSet = options != null && commonConfig?.LAYOUT_TYPE !== 'narrow';
    const defaultContainerHeight = 300;
    const defaultContainerWidth = sizesCanBeSet ? options.width : null;
    const minWidth = sizesCanBeSet ? options.minWidth : null;
    const maxWidth = sizesCanBeSet ? options.maxWidth : null;

    const { containerSize, initialContainerHeight, initialContentWidth } = useResizeAndMoveHook(
        {
            dialogName: options?.name,
            draggable: false,
            resizable: true,
            closeOnWindowResize: true
        },
        defaultContainerHeight,
        defaultContainerWidth,
        dialogRef,
        undefined,
        undefined,
        100,
        minWidth
    );

    const getPopupCloseHanlder = (callback: Function, id: string) => {
        return () => {
            if (typeof callback === 'function') {
                callback();
            }

            props.glob.DialogActions.destroyPopup(id);
        };
    };

    const handleOnModalFadeClick = (e) => {
        // Modal Fade is a pad under not modal popup dialogs that must be closed by outside click.
        // Modal Fade is equal to the size of screen so it gets outside clicks.
        // However outside click must not only close the popup, but also be a click for an element under Modal Fade.
        // This code hides Modal Fade and simulates mouse click on the element in position.
        $(modalFadeRef.current).hide();

        const next = document.elementFromPoint(e.pageX, e.pageY);

        // Simulate mouse click.
        ['mousedown', 'click', 'mouseup'].forEach((mouseEventType) =>
            next.dispatchEvent(
                new MouseEvent(mouseEventType, {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                    buttons: 1
                })
            )
        );
    };

    const PopupComponent = options.component;
    const { modalStyle, containerStyle, contentStyle, bodyStyle } = getStyles(
        initialContainerHeight,
        initialContentWidth,
        containerSize.height,
        undefined,
        undefined,
        !sizesCanBeSet
    );

    let zIndex = obj.zIndex;
    let top = 0;
    let left = 0;
    let parentComponentHeight = 0;

    if (options != null) {
        if (zIndex == null) {
            zIndex = options.zIndex;
        }

        if (options.top != null) {
            top = options.top;
        }

        if (options.parentComponentHeight != null) {
            parentComponentHeight = options.parentComponentHeight - 2;
        }

        if (options.left != null) {
            containerStyle.left = left = options.left;
        }
    }

    if (sizesCanBeSet) {
        if (minWidth != null) {
            containerStyle.minWidth = minWidth;
        }

        if (maxWidth != null) {
            containerStyle.maxWidth = maxWidth;
        } else {
            const windowWidth = $(document.body).width();

            containerStyle.maxWidth = windowWidth - left - 20;
        }
    }

    let bottom = 0;
    let downwardDirection = true;

    if (windowHeight - top - parentComponentHeight < 150) {
        // Not enough space below.
        bottom = top - parentComponentHeight;

        if (bottom > 150) {
            // Enough space above.
            downwardDirection = false;
        }
    }

    if (downwardDirection) {
        top += parentComponentHeight;
        containerStyle.top = top;
        bottom = windowHeight - 20;
    } else {
        modalStyle.height = '100%';
        containerStyle.position = 'absolute';
        containerStyle.bottom = windowHeight - top + 2;
        containerStyle.maxHeight = (windowHeight + containerStyle.bottom) / 2;
    }

    return (
        <div key={obj.id} className="dialog-popup">
            <div
                className="modal fade show"
                ref={modalFadeRef}
                role="dialog"
                style={{ zIndex: zIndex }}
                onClick={handleOnModalFadeClick}
            >
                <div className="modal-dialog" style={containerStyle}>
                    <div className="modal-content" ref={dialogRef} style={contentStyle}>
                        <div className="modal-body container-fluid" style={bodyStyle}>
                            <PopupComponent
                                zIndex={zIndex}
                                options={options}
                                close={getPopupCloseHanlder(options.close, props.id)}
                                glob={props.glob}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function Dialogs(props: { glob: IGlob; tabID?: number; pinNavigationBar?: boolean }) {
    props.glob.DialogsStore.useSimpleStoreStateHook();

    const dialogsRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleOnWindowResize = () => {
            $('.listen-container-resize').trigger('container-resize');
        };

        // OE - prevent right-click for this DIV
        const dlg = dialogsRef.current;

        $(dlg).contextmenu(false);
        $(window).on('resize', handleOnWindowResize);

        return () => {
            $(window).off('resize', handleOnWindowResize);
        };
    }, []);

    const getHandler = (callback: () => JQueryDeferred<any>, id: string) => {
        return () => {
            const def = callback != null ? callback() : null;

            props.glob.DialogActions.activateDialog(id);

            $.when(def).done(() => {
                props.glob.DialogActions.destroyDialog(id);
            });
        };
    };

    const getCloseHanlder = (callback: (...args) => void, id: string, cleanNotification = false) => {
        return (vals?) => {
            if (typeof callback === 'function') {
                callback(vals);
            }

            if (cleanNotification) {
                props.glob.AppActions.cleanWidgetNotifications(null);
            }

            props.glob.DialogActions.destroyDialog(id);
        };
    };

    const transformCallback = (dialog: IDialog, callback: (args?: any) => any) => {
        return () => {
            // obj will be mutated,
            // confirmValueGetter may be set by Component during
            // its lifecycle
            if (typeof dialog.confirmValueGetter !== 'function') {
                return callback();
            } else {
                return callback(dialog.confirmValueGetter());
            }
        };
    };

    const getConfirmDialog = (obj: IDialog, id: string) => {
        return (
            <ConfirmDialog
                key={'confirm-difalog_' + id}
                obj={obj}
                draggable={true}
                resizable={false}
                onSuccess={getHandler(obj.success, id)}
                onFail={getHandler(obj.fail, id)}
                onCancel={getCloseHanlder(obj.cancel, id)}
            />
        );
    };

    const getOnProcessDirSelectHandler = (obj: IDialog, id: string) => {
        return (...args) => {
            props.glob.DialogActions.destroyDialog(id);

            obj.success.apply(obj, args);
        };
    };

    const getOnHideDirsSelectHandler = (obj: IDialog, id: string) => {
        return () => {
            props.glob.DialogActions.destroyDialog(id);
            obj.fail();
        };
    };

    const getWorkspaceDirSelectDialog = (obj: IDialogWorkspaceDirSelect, id: string) => {
        const config = obj.config || {};

        let expandedItems = {}; // like: {4:true, 29: true};
        let selectedFolder = config.dir;

        _.each(config.wsPathDirId, (dirIdOne) => {
            expandedItems[dirIdOne] = true;
            selectedFolder = dirIdOne;
        });

        return (
            <DirsWorkspacesInner
                key={'dirs-ws-inner-' + id}
                name={config.name}
                title={obj.title}
                selectTitle={config.selectTitle}
                dir={selectedFolder}
                expandedItems={expandedItems}
                nameSelect={config.nameSelect}
                overwrite={config.overwrite}
                showMyFolders={true}
                overwriteShow={config.overwriteShow}
                hiddenScroll={config.hiddenScroll}
                allowSelectItem={config.allowSelectItem}
                allowOpenWorkspace={config.allowOpenWorkspace}
                modeSelectWorkspace={config.modeSelectWorkspace}
                zIndex={getZIndex(obj)}
                onHide={getOnHideDirsSelectHandler(obj, id)}
                onDirSelect={getOnProcessDirSelectHandler(obj, id)}
                glob={props.glob}
            />
        );
    };

    const getTemplateDirSelectDialog = (obj: IDialogTemplateDirSelect, id: string) => {
        const config = obj.config || {};

        let selectMode = true;
        let selectTemplateMode = false;

        if (config.selectTemplateMode) {
            selectMode = false;
            selectTemplateMode = true;
        }

        return (
            <DirsTemplatesInner
                key={'dirs-templates-inner' + id}
                title={obj.title}
                selectMode={selectMode}
                selectTemplateMode={selectTemplateMode}
                selectTitle={config.selectTitle}
                nameSelect={config.nameSelect}
                name={config.name}
                overwrite={config.overwrite}
                overwriteShow={config.overwriteShow}
                allowSelectItem={config.allowSelectItem}
                zIndex={getZIndex(obj)}
                onHide={getOnHideDirsSelectHandler(obj, id)}
                onDirSelect={getOnProcessDirSelectHandler(obj, id)}
                onTemplateSelect={getOnProcessDirSelectHandler(obj, id)}
                glob={props.glob}
            />
        );
    };

    const getCustomDialog = (dialog: IDialog, id: string) => {
        const config = dialog.config;
        const draggable = config != null && config.draggable != null ? config.draggable : true;
        const resizable = config != null && config.resizable != null ? config.resizable : true;

        props.glob.AppActions.cleanWidgetNotifications(null);

        return (
            <CustomDialog
                key={'custom-dialog-' + id}
                obj={dialog}
                success={getHandler(transformCallback(dialog, dialog.success), id)}
                fail={getHandler(dialog.fail, id)}
                close={getCloseHanlder(dialog.cancel, id, true)}
                draggable={draggable}
                resizable={resizable}
                pinNavigationBar={props.pinNavigationBar}
                glob={props.glob}
            />
        );
    };

    const getNewCustomDialog = (dialog: IDialog, id: string) => {
        const config = dialog.config;
        const draggable = config != null && config.draggable != null ? config.draggable : true;
        const resizable = config != null && config.resizable != null ? config.resizable : true;

        props.glob.AppActions.cleanWidgetNotifications(null);

        return (
            <NewCustomDialog
                key={'custom-dialog-' + id}
                obj={dialog}
                success={getHandler(transformCallback(dialog, dialog.success), id)}
                fail={getHandler(dialog.fail, id)}
                close={getCloseHanlder(dialog.cancel, id, true)}
                draggable={draggable}
                resizable={resizable}
                pinNavigationBar={props.pinNavigationBar}
                glob={props.glob}
            />
        );
    };

    const getFullScreen = (obj, id: string) => {
        const Component = obj.config.Component;

        const dialogStyle = {
            display: 'block',
            zIndex: getZIndex(obj)
        };

        return (
            <div className="modal modal-fit fade show" style={dialogStyle} role="dialog" key={'full-screan-dlg-' + id}>
                <Component
                    onExitFullScreen={getCloseHanlder(obj.cancel, id)}
                    isFullScreen={true}
                    {...obj.config.props}
                />
            </div>
        );
    };

    const getDialog = (obj: IDialog, id: string) => {
        let type: string;

        if (typeof obj.type === 'string') {
            type = obj.type;
        } else if (typeof obj.type === 'number') {
            type = DIALOG_TYPE_DICT_ENUM[obj.type];
        }

        if (type == null) {
            return;
        }

        switch (type) {
            case 'custom':
                return getCustomDialog(obj, id);

            case 'newCustom':
                return getNewCustomDialog(obj, id);                

            case 'confirm':
                return getConfirmDialog(obj, id);

            case 'workspaceDirSelect':
                return getWorkspaceDirSelectDialog(obj, id);

            case 'templateDirSelect':
                return getTemplateDirSelectDialog(obj, id);

            case 'fullScreen':
                return getFullScreen(obj, id);
        }
    };

    const getPopupCloseHanlder = (callback: Function, id: string) => {
        return () => {
            if (typeof callback === 'function') {
                callback();
            }

            props.glob.DialogActions.destroyPopup(id);
        };
    };

    const getCustomPopup = (obj: IPopup, id: string) => {
        const { options } = obj;
        const PopupComponent = options.component;

        return (
            <PopupComponent
                key={id}
                zIndex={obj.zIndex}
                options={options}
                close={getPopupCloseHanlder(options.close, id)}
                glob={props.glob}
            />
        );
    };

    const getPopup = (obj: IPopup, id: string) => {
        if (obj == null) {
            return;
        }

        switch (obj.type) {
            case 'custom':
                return getCustomPopup(obj, id);

            default:
                return <DialogPopup key={id} id={id} obj={obj} draggable={false} resizable={true} glob={props.glob} />;
        }
    };

    const handleOnContainerClick = (e) => {
        reactGlobalClick.cancel(1);
    };

    const { dialogs, popups } = props.glob.DialogsStore.getState();
    const { tabID } = props;

    let dialogsDiv: React.JSX.Element;

    if (dialogs.size > 0) {
        const dialogElements: React.JSX.Element[] = [];

        dialogs.forEach((dialog, id) => {
            const dialogTabID = dialog.config?.tabID;

            if ((tabID == null && dialogTabID == null) || tabID === dialogTabID) {
                dialogElements.push(getDialog(dialog, id));
            }
        });

        if (dialogElements.length > 0) {
            let modalBackdrop: React.JSX.Element;

            if (tabID == null) {
                modalBackdrop = <div className="modal-backdrop fade show"></div>;
            }

            dialogsDiv = (
                <div className="popup-dialog popup-overflow-controled">
                    {dialogElements}
                    {modalBackdrop}
                </div>
            );
        }
    }

    let popupElements: React.JSX.Element[];

    if (tabID == null) {
        popupElements = Array.from(popups, ([id, obj]) => {
            return getPopup(obj, id);
        });
    }

    return (
        <div className="dialogs" ref={dialogsRef} onClick={handleOnContainerClick}>
            {dialogsDiv}
            {popupElements}
        </div>
    );
}
